import { FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IConnectStepProps } from '../../types';
import { CHANNEL_INFO, T_KEYS } from '../../constants';
import {
  ConnectCard,
  ConnectCardHeader,
  ConnectCardLogo,
  ConnectCardTitle,
  ConnectCardDescription,
  TermsCheckbox,
  ButtonsRow,
  PrimaryButton,
  SecondaryButton,
} from '../../styles';

// Import unified channel logo components
import { ChannelLogo, ChannelLogoType } from '../ChannelLogos';
import { TermsModal } from '../Modals';

const ConnectStep: FC<IConnectStepProps> = memo(({
  channelName,
  onCancel,
  onConnect,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const channelInfo = CHANNEL_INFO[channelName];
  const displayName = isRtl ? channelInfo.displayNameAr : channelInfo.displayName;

  // Map channelName to ChannelLogoType
  const channelLogoType: ChannelLogoType = channelName as ChannelLogoType;

  const getDescription = () => {
    const descriptionKeys: Record<string, string> = {
      airbnb: T_KEYS.AIRBNB_CONNECT_DESC,
      booking: T_KEYS.BOOKING_CONNECT_DESC,
      gathern: T_KEYS.GATHERN_CONNECT_DESC,
    };
    const key = descriptionKeys[channelName];
    return key ? t(key, { channel: displayName }) : '';
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  return (
    <>
      <ConnectCard>
        <ConnectCardHeader>
          <ChannelLogo channel={channelLogoType} size="lg" rounded />
          <ConnectCardTitle>
            {t(T_KEYS.CONNECT_CHANNEL_WITH_MSOOL, { channel: displayName })}
          </ConnectCardTitle>
        </ConnectCardHeader>

        <ConnectCardDescription style={{ whiteSpace: 'pre-line' }}>
          {getDescription()}
        </ConnectCardDescription>

        <TermsCheckbox>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <span>
            {t(T_KEYS.I_AGREE_TO)}
            <button type="button" className="terms-link" onClick={handleTermsClick}>
              {t(T_KEYS.TERMS_OF_SERVICE)}
              {" "}
              {t(T_KEYS.AND)}
              {" "}
              {t(T_KEYS.PRIVACY_POLICY)}
            </button>
            {t(T_KEYS.OF_MSOOL)}
          </span>
        </TermsCheckbox>

        <ButtonsRow>
          <SecondaryButton onClick={onCancel}>
            {t(T_KEYS.CANCEL)}
          </SecondaryButton>
          <PrimaryButton
            $disabled={!termsAccepted || isLoading}
            onClick={onConnect}
            disabled={!termsAccepted || isLoading}
          >
            {isLoading ? t(T_KEYS.CONNECTING_LOADING) : t(T_KEYS.CONNECT)}
          </PrimaryButton>
        </ButtonsRow>
      </ConnectCard>

      {/* Terms of Service Modal */}
      <TermsModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        type="terms"
      />
    </>
  );
});

ConnectStep.displayName = 'ConnectStep';

export default ConnectStep;
